// Lot 638 — Boutons de volume physiques → le son se réactive (Reels, vidéos du fil, stories, directs).
// Le natif émet `yalolaVolumeKey` {detail:{direction:"up"|"down"}} (Android MainActivity.onKeyDown, iOS KVO sur
// AVAudioSession.outputVolume) ; le volume système change normalement. Une seule cible réagit : la plus prioritaire
// parmi celles montées (story > direct > reels > fil) — sinon la feuille story ouverte réactiverait aussi le fil dessous.
// Sur le web rien n'arrive (aucun accès navigateur aux touches de volume) : les boutons 🔇 restent le seul chemin.
import { useEffect, useRef } from "react";
import { guardOn, subscribeGuards } from "@/lib/guards";
import { startNativeVolumeKeys, stopNativeVolumeKeys, onNativeEvent } from "@/lib/yalolaNative";

export const VOLUME_KEY_EVENT = "yalolaVolumeKey";
export const VOLUME_PRIORITY = { FEED: 10, REELS: 20, LIVE: 25, STORY: 30 };

const targets = new Set();
let installed = false;

export const handleVolumeKey = (direction = "up") => {
  let best = null;
  for (const tg of targets) if (!best || tg.priority > best.priority) best = tg;
  if (!best) return false;
  try { best.fn(direction); } catch { /* noop */ }
  return true;
};

// Lot 671 — garde-fou admin « volume_keys ». Lot 678 — iPhone : la session audio + l'observateur natifs ne tournent
// QUE pendant un écran média (Reels, story ouverte, direct spectateur : cible de priorité ≥ REELS) et s'arrêtent en le
// quittant — plus jamais au lancement ni sur le fil (plugin YalolaNative start/stop). Android : simples touches.
const MEDIA_SCREEN_PRIORITY = VOLUME_PRIORITY.REELS;
let nativeStarted = false;
const mediaScreenOpen = () => { for (const tg of targets) if (tg.priority >= MEDIA_SCREEN_PRIORITY) return true; return false; };
const syncNative = () => {
  const want = guardOn("volume_keys") && mediaScreenOpen();
  if (want && !nativeStarted) {
    nativeStarted = true;
    startNativeVolumeKeys().then((ok) => { if (!ok) nativeStarted = false; });
  } else if (!want && nativeStarted) {
    nativeStarted = false;
    stopNativeVolumeKeys();
  }
};
export const nativeVolumeKeysActive = () => nativeStarted;
export const installVolumeKeys = () => {
  if (installed || typeof window === "undefined") return;
  installed = true;
  const onKey = (direction) => { if (guardOn("volume_keys")) handleVolumeKey(direction === "down" ? "down" : "up"); };
  window.addEventListener(VOLUME_KEY_EVENT, (e) => onKey(e?.detail?.direction));
  onNativeEvent("volumeKey", (d) => onKey(d?.direction));
  syncNative();
  subscribeGuards(syncNative);
};

export const registerVolumeKeyTarget = (fn, priority) => {
  const tg = { fn, priority };
  targets.add(tg);
  syncNative();
  return () => { targets.delete(tg); syncNative(); };
};

// `unmute` est appelé (direction en argument) quand une touche de volume est pressée et que cette cible est la plus prioritaire.
export const useVolumeKeyUnmute = (active, priority, unmute) => {
  const ref = useRef(unmute);
  ref.current = unmute;
  useEffect(() => {
    if (!active) return undefined;
    return registerVolumeKeyTarget((d) => ref.current?.(d), priority);
  }, [active, priority]);
};

export const _resetVolumeKeysForTests = () => { targets.clear(); nativeStarted = false; };
